import { Card } from '@/components/ui/card';
import { QuestionType } from '../QuestionCoachModal';
import { Users, Building2, TrendingUp, BarChart3, Trophy, Scale, Sigma } from 'lucide-react';

interface QuestionTypeStepProps {
  selectedType?: QuestionType;
  onSelect: (type: QuestionType) => void;
  enabledTypes?: QuestionType[]; // Optional: if provided, only these types will be shown
}

const QUESTION_TYPES = [
  {
    type: 'player_selection' as QuestionType,
    title: 'Player Selection',
    description: 'Which player will have the most of a stat?',
    icon: Users,
    example: 'Which player will have the most passing yards?',
  },
  {
    type: 'team_selection' as QuestionType,
    title: 'Team Selection',
    description: 'Which team will have the most of a stat?',
    icon: Building2,
    example: 'Which team will score the most points?',
  },
  {
    type: 'player_over_under' as QuestionType,
    title: 'Player Over/Under',
    description: 'Will a player go over or under a threshold?',
    icon: TrendingUp,
    example: 'Will Patrick Mahomes throw for over 300 yards?',
  },
  {
    type: 'team_over_under' as QuestionType,
    title: 'Team Over/Under',
    description: 'Will a team go over or under a threshold?',
    icon: BarChart3,
    example: 'Will the Chiefs score over 27.5 points?',
  },
  // Main markets (DKPLAT-500) — graded off the final score, all sports.
  {
    type: 'moneyline' as QuestionType,
    title: 'Moneyline',
    description: 'Which team will win the match?',
    icon: Trophy,
    example: 'Which team will win? (Home / Draw / Away)',
  },
  {
    type: 'spread' as QuestionType,
    title: 'Spread',
    description: 'Which team will cover the handicap?',
    icon: Scale,
    example: 'Will the Chiefs cover -3.5?',
  },
  {
    type: 'total' as QuestionType,
    title: 'Total',
    description: 'Over/Under the combined match score.',
    icon: Sigma,
    example: 'Will over 2.5 total goals be scored?',
  },
];

// Default to all auto-gradable question types
const DEFAULT_ENABLED_TYPES: QuestionType[] = [
  'player_selection',
  'team_selection',
  'player_over_under',
  'team_over_under',
  'moneyline',
  'spread',
  'total',
];

export function QuestionTypeStep({
  selectedType,
  onSelect,
  enabledTypes = DEFAULT_ENABLED_TYPES,
}: QuestionTypeStepProps) {
  // Filter question types based on enabled types
  const availableQuestionTypes = QUESTION_TYPES.filter(qt => enabledTypes.includes(qt.type));
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Choose the type of question you want to create from the sports feed
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {availableQuestionTypes.map(questionType => {
          const Icon = questionType.icon;
          const isSelected = selectedType === questionType.type;

          return (
            <Card
              key={questionType.type}
              data-testid={`question-type-${questionType.type}`}
              className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-primary bg-primary/5' : ''
              }`}
              onClick={() => onSelect(questionType.type)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`p-2 rounded-lg ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                >
                  <Icon className="h-5 w-5" />
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold mb-1">{questionType.title}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{questionType.description}</p>
                  <p className="text-xs text-muted-foreground italic">{questionType.example}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
